import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  class: "level-1-container",
  role: "menubar"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("a", {
  href: "#",
  class: "level-1"
}, "   ", -1)
const _hoisted_3 = ["href", "aria-expanded", "onKeydown"]
const _hoisted_4 = {
  key: 1,
  class: "level-2-container",
  role: "menu"
}
const _hoisted_5 = ["href", "onKeydown"]
const _hoisted_6 = ["href", "target", "onKeydown"]
const _hoisted_7 = {
  key: 0,
  class: "level-1 has-children my-account"
}
const _hoisted_8 = { class: "user-icon-container" }
const _hoisted_9 = {
  class: "level-2-container",
  role: "menu"
}
const _hoisted_10 = { class: "level-2" }
const _hoisted_11 = { class: "level-2" }
const _hoisted_12 = { class: "level-2" }
const _hoisted_13 = { class: "level-2" }
const _hoisted_14 = { class: "level-2" }
const _hoisted_15 = {
  key: 0,
  href: "/user/login",
  class: "header-login-cta btn btn--primary"
}
const _hoisted_16 = {
  class: "header-cart-cta",
  href: "/cart",
  "aria-label": "View Cart"
}

import { ref, onMounted, onUnmounted, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { logout } from '../../../../global/js/magento-api-library/auth/logout'
import useAuth from '../../composables/useAuth'


const __default__ = {
  name: 'HeaderNavigation',
}

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    navItems: null,
    navUtility: null
  },
  setup(__props: any) {

const props = __props as {
  navItems: Array<{
    id: number
    title: string
    url: string
    children?: Array<{
      id: number
      title: string
      url: string
    }>
  }>
  navUtility: Array<{
    id: number
    title: string
    url: string
  }>
}



const { isLoggedIn } = useAuth()

const menuItems = ref<HTMLElement[]>([])
const submenuItems = ref<HTMLElement[][]>([])
const activeLevel1Index = ref<number | null>(null)

const mobileBreakpoint = 1024
const navContainer = ref<HTMLElement | null>(null)
const mobileNavActive = ref(false)

/**
 * Handles enabling/disabling the mobile navigation
 * Timeout is necessary for reactive timing
 */
const toggleMobileNav = () => {
  setTimeout(() => {
    mobileNavActive.value = true
  }, 100)
}

/**
 * Handles the mobile click event on the top level nav links
 */
let lastTapTime = 0

const handleNavClick = (event: MouseEvent) => {
  // Skip if not mobile
  if (window.innerWidth >= mobileBreakpoint) {
    return
  }

  const link = event.currentTarget as HTMLElement
  const parentLi = link.closest('li')

  if (!parentLi) return

  const now = Date.now()
  const DOUBLE_TAP_DELAY = 300 //ms

  const isActive = parentLi.classList.contains('active')

  // If the item is active and the user double taps, do nothing
  if (isActive && now - lastTapTime < DOUBLE_TAP_DELAY) {
    return
  }

  event.preventDefault()

  // Toggle active class
  if (!isActive) {
    parentLi.classList.add('active')
    menuItems.value.forEach((item) => {
      if (item !== parentLi) {
        item.classList.remove('active')
      }
    })
  } else {
    parentLi.classList.remove('active')
  }

  // Update last tap time
  lastTapTime = now
}

/**
 * Handles removing active class from all level-1 items
 */
const removeActiveClasses = () => {
  const activeItems = navContainer.value?.querySelectorAll('.level-1.active')
  activeItems?.forEach((item) => {
    item.classList.remove('active')
  })

  document.body.classList.remove('no-scroll')
}

/**
 * Handles all window resize events
 */
const handleResize = () => {
  if (window.innerWidth >= mobileBreakpoint) {
    // If the screen is resized, reset the active state for main nav top level items
    removeActiveClasses()
    // Close mobile nav if it was open
    mobileNavActive.value = false
  }
}

/**
 * Handles the keydown event for top level nav items
 */
const handleTopLevelKeydown = (event: KeyboardEvent, level1Index: number) => {
  switch (event.key) {
    case 'ArrowRight':
      event.preventDefault()
      focusLevel1((level1Index + 1) % menuItems.value.length)
      break
    case 'ArrowLeft':
      event.preventDefault()
      focusLevel1(
        (level1Index - 1 + menuItems.value.length) % menuItems.value.length,
      )
      break
    case 'Enter':
    case 'ArrowDown':
      event.preventDefault()
      openSubMenu(level1Index)
      focusLevel2(level1Index, 0)
      break
  }
}

/**
 * Handles the keydown event for submenu items
 */
const handleSubmenuKeydown = (
  event: KeyboardEvent,
  level1Index: number,
  level2Index: number,
) => {
  const submenu = submenuItems.value[level1Index]
  if (!submenu) {
    return
  }

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      focusLevel2(level1Index, (level2Index + 1) % submenu.length)
      break

    case 'ArrowUp':
      event.preventDefault()
      focusLevel2(
        level1Index,
        (level2Index - 1 + submenu.length) % submenu.length,
      )
      break

    case 'Escape':
      event.preventDefault()
      closeSubMenu(level1Index)
      focusLevel1(level1Index)
      break

    case 'ArrowLeft':
      event.preventDefault()
      closeSubMenu(level1Index)
      focusLevel1(
        (level1Index - 1 + menuItems.value.length) % menuItems.value.length,
      )
      break

    case 'ArrowRight':
      event.preventDefault()
      closeSubMenu(level1Index)
      focusLevel1((level1Index + 1) % menuItems.value.length)
      break
  }
}

/**
 * Focuses the top level menu item
 */
const focusLevel1 = (index: number) => {
  menuItems.value[index]?.querySelector('a')?.focus()
}

/**
 * Focuses the submenu item
 */
const focusLevel2 = (level1Index: number, level2Index: number) => {
  submenuItems.value[level1Index]?.[level2Index]?.focus()
}

const openSubMenu = (index: number) => {
  // Close any other open menus
  menuItems.value.forEach((item, i) => {
    if (i !== index) item.classList.remove('active')
  })

  menuItems.value[index]?.classList.add('active')
  activeLevel1Index.value = index
}

/**
 * Closes the submenu
 */
const closeSubMenu = (index: number) => {
  menuItems.value[index]?.classList.remove('active')
  if (activeLevel1Index.value === index) {
    activeLevel1Index.value = null
  }
}

const closeMobileNav = () => {
  mobileNavActive.value = false
  removeActiveClasses()
}

/**
 * Handles closing the submenu when focus is lost
 */
const handleFocusOut = (event: FocusEvent) => {
  const currentMenu = menuItems.value[activeLevel1Index.value ?? -1]
  if (!currentMenu) {
    return
  }

  const relatedTarget = event.relatedTarget as Node | null

  if (!relatedTarget || !currentMenu.contains(relatedTarget)) {
    currentMenu.classList.remove('active')
    activeLevel1Index.value = null
  }
}

/**
 * Handles closing the submenu when clicking anywhere outside of the submenu
 */
const handleDocumentClick = (event: MouseEvent) => {
  const target = event.target as Node
  const currentMenu = menuItems.value[activeLevel1Index.value ?? -1]

  if (currentMenu && !currentMenu.contains(target)) {
    currentMenu.classList.remove('active')
    activeLevel1Index.value = null
  }
}

/**
 * Watcher for activating no-scroll class on body when mobile nav is active
 */
watch(mobileNavActive, (newVal) => {
  document.body.classList.toggle('no-scroll', newVal)
})

onMounted(() => {
  // Build the submenus
  const topLevelItems = navContainer.value?.querySelectorAll('.level-1')

  if (topLevelItems) {
    menuItems.value = Array.from(topLevelItems) as HTMLElement[]
    submenuItems.value = menuItems.value.map((li) => {
      const submenuLinks = li.querySelectorAll('.level-2 a')
      return Array.from(submenuLinks) as HTMLElement[]
    })
  }

  // Set Event Listeners
  window.addEventListener('resize', handleResize)
  document.addEventListener('click', handleDocumentClick)
  navContainer.value?.addEventListener('focusout', handleFocusOut)
})

onUnmounted(() => {
  // Cleanup Event Listeners
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('click', handleDocumentClick)
  navContainer.value?.removeEventListener('focusout', handleFocusOut)
})

return (_ctx: any,_cache: any) => {
  const _component_Minicart = _resolveComponent("Minicart")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("nav", {
      ref_key: "navContainer",
      ref: navContainer,
      class: _normalizeClass(['nav', mobileNavActive.value ? 'mobile-active' : ''])
    }, [
      _createElementVNode("ul", _hoisted_1, [
        _createElementVNode("li", {
          class: "level-1 mobile-only",
          onClick: closeMobileNav
        }, [
          _hoisted_2,
          _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'times'] })
        ]),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.navItems, (item, index) => {
          return (_openBlock(), _createElementBlock("li", {
            key: item.id,
            class: _normalizeClass([
          'level-1',
          item.children && item.children.length ? 'has-children' : '',
        ])
          }, [
            _createElementVNode("a", {
              href: item.url,
              "aria-expanded": activeLevel1Index.value === index,
              tabindex: "0",
              role: "menuitem",
              "aria-haspopup": "true",
              onKeydown: ($event: any) => (handleTopLevelKeydown($event, index)),
              onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (handleNavClick($event)), ["prevent"]))
            }, _toDisplayString(item.title), 41, _hoisted_3),
            (item.children && item.children.length)
              ? (_openBlock(), _createBlock(_unref(FontAwesomeIcon), {
                  key: 0,
                  icon: ['fas', 'caret-down'],
                  class: "carrot-icon"
                }))
              : _createCommentVNode("", true),
            (item.children && item.children.length)
              ? (_openBlock(), _createElementBlock("ul", _hoisted_4, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.children, (child, childIndex) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: child.id,
                      class: "level-2"
                    }, [
                      _createElementVNode("a", {
                        href: child.url,
                        tabindex: "0",
                        role: "menuitem",
                        onKeydown: _withModifiers(($event: any) => (handleSubmenuKeydown($event, index, childIndex)), ["stop"])
                      }, _toDisplayString(child.title), 41, _hoisted_5)
                    ]))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ], 2))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.navUtility, (item, index) => {
          return (_openBlock(), _createElementBlock("li", {
            key: item.id,
            class: "level-1"
          }, [
            _createElementVNode("a", {
              href: item.url,
              target: item.newWindow ? '_blank' : '_self',
              tabindex: "0",
              onKeydown: ($event: any) => (handleTopLevelKeydown($event, index + __props.navItems.length))
            }, _toDisplayString(item.title), 41, _hoisted_6)
          ]))
        }), 128)),
        (_unref(isLoggedIn))
          ? (_openBlock(), _createElementBlock("li", _hoisted_7, [
              _createElementVNode("span", _hoisted_8, [
                _createVNode(_unref(FontAwesomeIcon), {
                  icon: ['fas', 'user'],
                  class: "user-icon"
                })
              ]),
              _createElementVNode("a", {
                href: "/user/profile",
                tabindex: "0",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (handleNavClick($event))),
                onKeydown: _cache[2] || (_cache[2] = ($event: any) => (
            handleTopLevelKeydown($event, __props.navItems.length + __props.navUtility.length)
          ))
              }, " My Account ", 32),
              _createVNode(_unref(FontAwesomeIcon), {
                icon: ['fas', 'caret-down'],
                class: "carrot-icon"
              }),
              _createElementVNode("ul", _hoisted_9, [
                _createElementVNode("li", _hoisted_10, [
                  _createElementVNode("a", {
                    href: "/user/profile#Subscriptions",
                    onKeydown: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (
                handleSubmenuKeydown(
                  $event,
                  __props.navItems.length + __props.navUtility.length,
                  0,
                )
              ), ["stop"])),
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (mobileNavActive.value = false))
                  }, " Subscriptions ", 32)
                ]),
                _createElementVNode("li", _hoisted_11, [
                  _createElementVNode("a", {
                    href: "/user/profile#OrderHistory",
                    onKeydown: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (
                handleSubmenuKeydown(
                  $event,
                  __props.navItems.length + __props.navUtility.length,
                  1,
                )
              ), ["stop"])),
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (mobileNavActive.value = false))
                  }, " Orders ", 32)
                ]),
                _createElementVNode("li", _hoisted_12, [
                  _createElementVNode("a", {
                    href: "/user/profile#PurchasedContent",
                    onKeydown: _cache[7] || (_cache[7] = _withModifiers(($event: any) => (
                handleSubmenuKeydown(
                  $event,
                  __props.navItems.length + __props.navUtility.length,
                  2,
                )
              ), ["stop"]))
                  }, " Content ", 32)
                ]),
                _createElementVNode("li", _hoisted_13, [
                  _createElementVNode("a", {
                    href: "/user/profile#MyTranscripts",
                    onKeydown: _cache[8] || (_cache[8] = _withModifiers(($event: any) => (
                handleSubmenuKeydown(
                  $event,
                  __props.navItems.length + __props.navUtility.length,
                  3,
                )
              ), ["stop"])),
                    onClick: _cache[9] || (_cache[9] = ($event: any) => (mobileNavActive.value = false))
                  }, " Transcripts ", 32)
                ]),
                _createElementVNode("li", _hoisted_14, [
                  _createElementVNode("a", {
                    href: "/",
                    onKeydown: _cache[10] || (_cache[10] = _withModifiers(($event: any) => (
                handleSubmenuKeydown(
                  $event,
                  __props.navItems.length + __props.navUtility.length,
                  4,
                )
              ), ["stop"])),
                    onClick: _cache[11] || (_cache[11] = 
//@ts-ignore
(...args) => (_unref(logout) && _unref(logout)(...args)))
                  }, " Log Out ", 32)
                ])
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", {
        class: _normalizeClass(['actions-container', !_unref(isLoggedIn) ? 'logged-out' : ''])
      }, [
        (!_unref(isLoggedIn))
          ? (_openBlock(), _createElementBlock("a", _hoisted_15, " Log In "))
          : (_openBlock(), _createElementBlock("a", {
              key: 1,
              href: "/",
              class: "header-logout-cta btn btn-primary",
              onClick: _cache[12] || (_cache[12] = 
//@ts-ignore
(...args) => (_unref(logout) && _unref(logout)(...args)))
            }, " Log Out ")),
        _createElementVNode("a", _hoisted_16, [
          _createVNode(_unref(FontAwesomeIcon), {
            icon: ['fas', 'shopping-cart'],
            size: "lg"
          }),
          _createVNode(_component_Minicart)
        ])
      ], 2)
    ], 2),
    (mobileNavActive.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "mobile-nav-overlay",
          onClick: _cache[13] || (_cache[13] = ($event: any) => (mobileNavActive.value = false))
        }))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: "mobile-nav-toggle",
      onClick: toggleMobileNav
    }, [
      _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'bars'] })
    ])
  ], 64))
}
}

})